import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Info } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';

// PR-S5 (Step 5) — per-project cross-check reports for the active project box:
// directional P&L, receivables aging, and finished-but-uninvoiced trips.

interface Pnl { month: string; revenueSar: number; fuelSar: number; maintenanceSar: number; expensesSar: number; costSar: number; profitSar: number }
interface Aging { buckets: { d0_30: number; d31_60: number; d61_90: number; d90plus: number }; totalSar: number; count: number }
interface Uninvoiced { items: Array<{ code: string; date: string | null; client: string | null; amount_sar: string | null }>; totalSar: number; count: number }

const CODE_TO_SCOPE: Record<string, string> = {
  'PRJ-NEOM': 'neom', 'PRJ-RIYADH': 'riyadh', 'PRJ-DUMPERS': 'dumpers',
  'PRJ-RIYADH-WATER': 'riyadh_water',
};
const sar = (n: number | null) => (n != null ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—');

export default function ProjectReportsPage() {
  const { project } = useCurrentProject();
  const scope = project?.code ? CODE_TO_SCOPE[project.code] : null;
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const pnl = useQuery({
    queryKey: ['report-pnl', scope, month], enabled: !!scope,
    queryFn: async () => (await apiClient.get<Pnl>('/api/v1/project-reports/pnl', { params: { scope, month } })).data,
  });
  const aging = useQuery({
    queryKey: ['report-aging', scope], enabled: !!scope,
    queryFn: async () => (await apiClient.get<Aging>('/api/v1/project-reports/aging', { params: { scope } })).data,
  });
  const uninv = useQuery({
    queryKey: ['report-uninvoiced', scope], enabled: !!scope,
    queryFn: async () => (await apiClient.get<Uninvoiced>('/api/v1/project-reports/uninvoiced', { params: { scope } })).data,
  });

  if (!scope) {
    return (
      <div className="min-h-screen w-full bg-background flex flex-col items-center justify-center text-center px-6">
        <Info className="h-8 w-8 text-muted-foreground mb-3" />
        <div className="text-lg font-medium">Pick a project first</div>
        <div className="text-sm text-muted-foreground mt-1 mb-4">Cross-check reports are per project. Open NEOM, Dumpers or Flatbed from the home.</div>
        <Button onClick={() => { window.location.href = '/home'; }}>Go to home</Button>
      </div>
    );
  }

  const p = pnl.data;
  const a = aging.data;

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">{project?.nameEn} · Cross-check</div>
          <div className="flex items-center gap-2">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard'; }} className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8 space-y-8">
        {/* P&L */}
        <section>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">P&amp;L · {month} <span className="text-amber-500 normal-case tracking-normal">(directional)</span></h3>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <div className="text-muted-foreground">Revenue</div><div className="text-end font-medium">{sar(p?.revenueSar ?? null)} SAR</div>
              <div className="text-muted-foreground">Fuel</div><div className="text-end text-muted-foreground">- {sar(p?.fuelSar ?? null)}</div>
              <div className="text-muted-foreground">Maintenance</div><div className="text-end text-muted-foreground">- {sar(p?.maintenanceSar ?? null)}</div>
              <div className="text-muted-foreground">Expenses</div><div className="text-end text-muted-foreground">- {sar(p?.expensesSar ?? null)}</div>
              <div className="font-medium border-t border-border pt-2 mt-1">Profit (directional)</div>
              <div className={`text-end font-semibold border-t border-border pt-2 mt-1 ${(p?.profitSar ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{sar(p?.profitSar ?? null)} SAR</div>
            </div>
            <div className="text-xs text-muted-foreground mt-2">Costs cover the attributable ones (fuel, maintenance, expenses). Driver pay and full allocation are not in this figure yet.</div>
          </div>
        </section>

        {/* Receivables aging */}
        <section>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Receivables aging</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[['0–30 days', a?.buckets.d0_30], ['31–60', a?.buckets.d31_60], ['61–90', a?.buckets.d61_90], ['90+ (oldest)', a?.buckets.d90plus]].map(([lbl, v], i) => (
              <div key={i} className={`rounded-xl border bg-card p-4 ${i === 3 && Number(v) > 0 ? 'border-rose-500/40' : 'border-border'}`}>
                <div className="text-xs text-muted-foreground">{lbl as string}</div>
                <div className={`text-xl font-semibold mt-1 ${i === 3 && Number(v) > 0 ? 'text-rose-600' : ''}`}>{sar((v as number) ?? null)}</div>
              </div>
            ))}
          </div>
          {a && <div className="text-sm text-muted-foreground mt-2">Total outstanding: <b>{sar(a.totalSar)} SAR</b> across {a.count}.</div>}
        </section>

        {/* Uninvoiced reconciliation */}
        <section>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Finished, not yet invoiced</h3>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground"><tr>
                <th className="text-start font-medium px-4 py-2">Code</th>
                <th className="text-start font-medium px-4 py-2">Date</th>
                <th className="text-start font-medium px-4 py-2">Client</th>
                <th className="text-end font-medium px-4 py-2">Amount</th>
              </tr></thead>
              <tbody>
                {uninv.isLoading && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
                {uninv.data && uninv.data.items.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">All finished trips are invoiced. Nothing leaking.</td></tr>}
                {uninv.data?.items.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.date ? String(r.date).slice(0, 10) : '—'}</td>
                    <td className="px-4 py-2">{r.client ?? '—'}</td>
                    <td className="px-4 py-2 text-end">{sar(r.amount_sar != null ? Number(r.amount_sar) : null)}</td>
                  </tr>
                ))}
              </tbody>
              {uninv.data && uninv.data.items.length > 0 && (
                <tfoot><tr className="border-t border-border bg-muted/20"><td className="px-4 py-2 font-medium" colSpan={3}>Uninvoiced revenue ({uninv.data.count})</td><td className="px-4 py-2 text-end font-semibold">{sar(uninv.data.totalSar)} SAR</td></tr></tfoot>
              )}
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
